import Uppy from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';

export default class UppyInstance {
    constructor(fileUploaderElement) {
        this.element = fileUploaderElement;
        this.uppy = null;
        this.fileInput = null;
        this.uploadButton = null;
        this.options = {};
        this.boundChooseFile = null;
        this.boundHandleFileChange = null;
    }

    async init() {
        this.unwrapAgendaLink();
        this.fileInput =
            this.element.querySelector('input[type="file"]') || this.element.children?.fileInput;
        this.uploadButton =
            this.element.querySelector('button') || this.element.children?.uploadButton;

        if (!this.fileInput || !this.uploadButton) {
            return;
        }

        this.options = this.getOptionsFromInput();
        this.setupEventListeners();
        await this.initUppy();

        this.uploadButton.textContent = this.options.uploadtitle;
        this.fileInput.multiple = this.options.multipleupload;
    }

    unwrapAgendaLink() {
        const directLink = this.element.querySelector('a[onclick*="Agenda.editForm"]');
        if (directLink && directLink.contains(this.element.querySelector('input[type="file"]'))) {
            directLink.removeAttribute('onclick');
            directLink.removeAttribute('href');
            while (directLink.firstChild) {
                this.element.insertBefore(directLink.firstChild, directLink);
            }
            directLink.remove();
        }

        const parent = this.element.parentElement;
        if (
            parent &&
            parent.tagName === 'A' &&
            parent.getAttribute('onclick')?.includes('Agenda.editForm')
        ) {
            parent.removeAttribute('onclick');
            parent.removeAttribute('href');
            parent.parentElement?.insertBefore(this.element, parent);
            parent.remove();
        }
    }

    getOptionsFromInput() {
        if (!this.fileInput) {
            throw new Error();
        }
        return {
            action: this.fileInput.getAttribute('data-prop-action') || '',
            backjs: this.fileInput.getAttribute('data-prop-backjs') || null,
            maxuploadsize: this.fileInput.getAttribute('data-prop-maxuploadsize') || null,
            alloweduploadtypes: this.fileInput.getAttribute('data-prop-alloweduploadtypes') || '',
            multipleupload: this.fileInput.getAttribute('data-prop-multipleupload') === 'true',
            uploadtitle:
                this.fileInput.getAttribute('data-prop-uploadtitle') || 'Importer un fichier',
        };
    }

    setupEventListeners() {
        this.boundChooseFile = this.chooseFile.bind(this);
        this.boundHandleFileChange = this.handleFileChange.bind(this);

        if (this.uploadButton) {
            this.uploadButton.removeEventListener('click', this.boundChooseFile);
        }
        if (this.fileInput) {
            this.fileInput.removeEventListener('change', this.boundHandleFileChange);
        }

        this.uploadButton.addEventListener('click', this.boundChooseFile);
        this.fileInput.addEventListener('change', this.boundHandleFileChange);
    }

    async initUppy() {
        const castedAllowedUploadTypes = this.options.alloweduploadtypes
            ? this.options.alloweduploadtypes.split(',').map((type) => `.${type}`)
            : null;

        const uiLang = window.sellsyapp.config.globalProperties.$store.state.corp.uiLang;

        const lang = uiLang === 'en' ? 'en_US' : `${uiLang}_${uiLang.toUpperCase()}`;
        const locale = (await import(`@uppy/locales/lib/${lang}.js`)).default;

        this.uppy = new Uppy({
            allowMultipleUploadBatches: false,
            restrictions: {
                allowedFileTypes: castedAllowedUploadTypes,
                maxTotalFileSize: this.options.maxuploadsize ? +this.options.maxuploadsize : null,
                maxNumberOfFiles: this.options.multipleupload ? null : 1,
            },
            locale,
        }).use(XHRUpload, {
            endpoint: this.options.action,
            fieldName: 'qqfile', // name required by the server
            onAfterResponse: (response) => {
                if (response.status === 200) {
                    const data = JSON.parse(response.response); //we have to set data here because backjs function use it implicitly
                    if (this.options.backjs) {
                        eval(this.options.backjs);
                    }
                }
            },
        });

        this.uppy.on('restriction-failed', (file, error) => {
            window.sellsyapp.config.globalProperties.$pushToast({
                type: 'error',
                icon: true,
                close: true,
                text: error.message,
            });
        });

        this.uppy.on('upload-success', (file, response) => {
            const icon = document.getElementById(`icon-${file.meta.id}`);
            if (icon) {
                icon.className = 'fa fa-check fa-gray fa-15';
            }
        });
    }

    chooseFile() {
        if (this.fileInput) {
            this.fileInput.click();
        }
    }

    handleFileChange(event) {
        const uploadFilesContainer = this.element;
        const files = Array.from(event.target.files);

        let containerLoadingFiles = this.element.children['container-loading-files'];
        if (!containerLoadingFiles) {
            containerLoadingFiles = document.createElement('div');
            containerLoadingFiles.id = 'container-loading-files';
            containerLoadingFiles.classList.add('flex-column');
            containerLoadingFiles.classList.add('ov-reset');
            containerLoadingFiles.style.gap = '8px';
            containerLoadingFiles.style.alignItems = 'flex-start';
        } else {
            containerLoadingFiles.innerHTML = '';
        }

        for (const file of files) {
            try {
                const id = crypto.randomUUID();
                this.uppy.addFile({
                    source: 'file input',
                    name: file.name,
                    type: file.type,
                    data: file,
                    meta: {
                        id,
                    },
                });

                const node = document.createElement('div');
                const icon = document.createElement('i');
                icon.className = 'fa fa-sync-alt fa-gray fa-15 fa-spin';
                icon.style.marginRight = '12px';
                icon.id = `icon-${id}`;

                node.innerHTML = `<span>${file.name}</span>`;
                node.prepend(icon);

                if (containerLoadingFiles) {
                    containerLoadingFiles.appendChild(node);
                }
            } catch (err) {
                if (!err.isRestriction) {
                    console.error(err);
                }
            }
        }

        uploadFilesContainer.appendChild(containerLoadingFiles);

        this.uppy.upload().finally(() => {
            this.uppy.clear();
            this.fileInput.value = null;
        });
    }

    destroy() {
        if (this.uppy) {
            this.uppy.destroy();
            this.uppy = null;
        }
        if (this.uploadButton && this.boundChooseFile) {
            this.uploadButton.removeEventListener('click', this.boundChooseFile);
        }
        if (this.fileInput && this.boundHandleFileChange) {
            this.fileInput.removeEventListener('change', this.boundHandleFileChange);
        }

        this.fileInput = null;
        this.uploadButton = null;
        this.options = {};
        this.boundChooseFile = null;
        this.boundHandleFileChange = null;
    }
}
